interface EmergencyFundInput {
  monthlyExpenses: number
  age: number
  employmentType: 'w2_stable' | 'w2_variable' | 'self_employed' | 'retired'
  incomeSources: 'single' | 'dual'
  dependents: 'none' | 'kids' | 'aging_parents' | 'both'
  housing: 'rent' | 'own'
  health: 'good' | 'fair' | 'chronic'
  jobMarket: 'easy' | 'moderate' | 'hard'
  currentSavings: number
  monthlyContribution: number // how much they can save per month
  savingsAPY: number // annual HYSA rate %
}

const EMPLOYMENT_SCORES: Record<string, number> = {
  w2_stable: 0,
  w2_variable: 1,
  self_employed: 3,
  retired: 6,
}

const INCOME_SCORES: Record<string, number> = {
  single: 1,
  dual: 0,
}

const DEPENDENT_SCORES: Record<string, number> = {
  none: 0,
  kids: 1,
  aging_parents: 2,
  both: 3,
}

const HOUSING_SCORES: Record<string, number> = {
  rent: 0,
  own: 1,
}

const HEALTH_SCORES: Record<string, number> = {
  good: 0,
  fair: 1,
  chronic: 2,
}

const JOB_MARKET_SCORES: Record<string, number> = {
  easy: 0,
  moderate: 1,
  hard: 2,
}

function getAgeScore(age: number): number {
  if (age < 26) return 0   // Young, flexible, low fixed costs
  if (age < 36) return 1   // Building career, starting to settle
  if (age < 46) return 2   // Mid-career, growing obligations
  if (age < 56) return 3   // Harder job transitions, higher expenses
  if (age < 65) return 5   // Pre-retirement, job search times spike
  return 7                  // Retirement age, fixed income, higher health costs
}

function getAgeLabel(age: number): string {
  if (age < 26) return 'Under 26'
  if (age < 36) return '26–35'
  if (age < 46) return '36–45'
  if (age < 56) return '46–55'
  if (age < 65) return '56–64'
  return '65+'
}

export function calculateEmergencyFund(input: EmergencyFundInput) {
  const base = 3

  const ageScore = getAgeScore(input.age)
  const employmentScore = EMPLOYMENT_SCORES[input.employmentType] ?? 0
  const incomeScore = INCOME_SCORES[input.incomeSources] ?? 0
  const dependentScore = DEPENDENT_SCORES[input.dependents] ?? 0
  const housingScore = HOUSING_SCORES[input.housing] ?? 0
  const healthScore = HEALTH_SCORES[input.health] ?? 0
  const jobMarketScore = JOB_MARKET_SCORES[input.jobMarket] ?? 0

  const totalScore = ageScore + employmentScore + incomeScore + dependentScore + housingScore + healthScore + jobMarketScore
  const rawMonths = base + totalScore
  const recommendedMonths = Math.min(24, Math.max(3, rawMonths))

  const targetAmount = recommendedMonths * input.monthlyExpenses
  const gap = Math.max(0, targetAmount - input.currentSavings)
  const progress = targetAmount > 0 ? Math.min(100, (input.currentSavings / targetAmount) * 100) : 100

  // Time to fully funded: how many months at their savings rate + APY
  let monthsToFunded = 0
  let interestEarned = 0
  if (gap > 0 && input.monthlyContribution > 0) {
    const r = (input.savingsAPY || 0) / 100 / 12
    if (r > 0) {
      // FV = PMT * ((1+r)^n - 1) / r  →  n = ln(1 + FV*r/PMT) / ln(1+r)
      monthsToFunded = Math.ceil(Math.log(1 + gap * r / input.monthlyContribution) / Math.log(1 + r))
      // Total contributed + interest to reach the gap
      const totalWithInterest = input.monthlyContribution * (Math.pow(1 + r, monthsToFunded) - 1) / r
      interestEarned = Math.round(totalWithInterest - (input.monthlyContribution * monthsToFunded))
    } else {
      monthsToFunded = Math.ceil(gap / input.monthlyContribution)
    }
  } else if (gap <= 0) {
    monthsToFunded = 0
  }

  // Source comparisons — age-adjusted per each source's actual guidance
  const age = input.age
  const isSelfEmployed = input.employmentType === 'self_employed'

  const moneyGuyMonths = age < 30 ? '3'
    : age < 40 ? '3-6'
    : age < 50 ? '6'
    : age < 60 ? '6-12'
    : '12-24'

  const fidelityMonths = age < 35 ? '3-6'
    : age < 50 ? '6-9'
    : '12+'

  const vanguardMonths = age < 40 ? '3-6'
    : age < 55 ? '6'
    : '6-12'

  const isDualStable = input.incomeSources === 'dual' && input.employmentType === 'w2_stable'
  const ramseyMonths = isDualStable ? '3' : isSelfEmployed ? '6' : '3-6'
  const ramseyDescription = isDualStable
    ? 'Baby Step 3 — 3 months OK with two stable incomes'
    : isSelfEmployed
    ? 'Baby Step 3 — lean toward 6 months with irregular income'
    : 'Baby Step 3 — save 3-6 months after paying off non-mortgage debt'

  const sourceComparisons = [
    { source: 'Money Guy Show', months: isSelfEmployed && age < 50 ? '6-12' : moneyGuyMonths, description: age < 30 ? 'Start lean, grow as income rises' : age < 50 ? 'Match to career stage & obligations' : 'Protect against longer job searches & health costs' },
    { source: 'Fidelity', months: fidelityMonths, description: age < 35 ? 'Build a baseline safety net early' : age < 50 ? 'Increase as family & financial obligations grow' : 'Larger cushion needed approaching retirement' },
    { source: 'Vanguard', months: vanguardMonths, description: age < 40 ? 'Cover essential expenses for 3-6 months' : 'Recommends more as re-employment timelines lengthen' },
    { source: 'Dave Ramsey', months: ramseyMonths, description: ramseyDescription },
    { source: 'This Calculator', months: String(recommendedMonths), description: 'Score-based analysis of your specific situation' },
  ]

  // Score breakdown for transparency
  const scoreBreakdown = [
    { factor: 'Age', value: getAgeLabel(input.age), score: ageScore },
    { factor: 'Employment Type', value: input.employmentType.replace(/_/g, ' '), score: employmentScore },
    { factor: 'Income Sources', value: input.incomeSources, score: incomeScore },
    { factor: 'Dependents', value: input.dependents.replace(/_/g, ' '), score: dependentScore },
    { factor: 'Housing', value: input.housing, score: housingScore },
    { factor: 'Health', value: input.health, score: healthScore },
    { factor: 'Job Market', value: input.jobMarket, score: jobMarketScore },
  ]

  return {
    recommendedMonths,
    targetAmount: Math.round(targetAmount),
    currentSavings: input.currentSavings,
    gap: Math.round(gap),
    progress: Math.round(progress * 10) / 10,
    monthsToFunded,
    interestEarned,
    monthlyContribution: input.monthlyContribution,
    monthlyExpenses: input.monthlyExpenses,
    totalScore,
    scoreBreakdown,
    sourceComparisons,
  }
}
